// eslint-disable-next-line react-hooks/exhaustive-deps
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';


const normalizeForSearch = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();


const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 15) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return out;
})();

export const Waitlist = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation();

  
  const isClient = user?.role === 'CLIENT';

  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);

  const visibleEntries = useMemo(() => {
    const now = new Date();
    return (Array.isArray(entries) ? entries : []).filter((e) => {
      const iso = e?.desiredSlots?.[0];
      if (!iso) return true;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return true;
      return d >= now;
    });
  }, [entries]);

  const [calendars, setCalendars] = useState([]);
  const [clients, setClients] = useState([]);

  const getClientLabel = useCallback((c) => {
    const full = (c?.name || c?.fullName || c?.displayName || '').toString().trim();
    const surname = (c?.surname || '').toString().trim();
    const email = (c?.email || '').toString().trim();
    const label = `${full}${surname ? ` ${surname}` : ''}`.trim();
    return (label || email || '').trim();
  }, []);
const sortedClients = useMemo(() => {
    const arr = Array.isArray(clients) ? [...clients] : [];
    arr.sort((a, b) =>
      getClientLabel(a).localeCompare(getClientLabel(b), 'es', { sensitivity: 'base' })
    );
    return arr;
  }, [clients, getClientLabel]);
const [createClientId, setCreateClientId] = useState('');

  const [clientQuery, setClientQuery] = useState('');
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [clientHighlight, setClientHighlight] = useState(-1);
    

  const filteredClients = useMemo(() => {
    const q = normalizeForSearch(clientQuery);
    if (!q) return sortedClients;
    return sortedClients.filter((c) =>
      normalizeForSearch(getClientLabel(c)).includes(q)
    );
  }, [sortedClients, clientQuery, getClientLabel]);

  const [createCalendarId, setCreateCalendarId] = useState('');
  const [createDate, setCreateDate] = useState('');
  const [createTime, setCreateTime] = useState('');
  const [createSlot, setCreateSlot] = useState('');

  const timeOptions = TIME_OPTIONS;

  useEffect(() => {
    if (!createDate || !createTime) {
      setCreateSlot('');
      return;
    }
    const dt = new Date(`${createDate}T${createTime}:00`);
    if (!isNaN(dt.getTime())) {
      setCreateSlot(dt.toISOString());
    }
  }, [createDate, createTime]);  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const calRes = await api.get('/calendars');
      const wlRes = await api.get('/waitlist');
      setCalendars(calRes.data || []);
      setEntries(wlRes.data || []);
      const clRes = await api.get('/clients');
      setClients(clRes.data || []);
      if (isClient && (clRes.data || []).length > 0) {
        const cid = clRes.data[0]?.id || '';
        if (cid) setCreateClientId(cid);
      }
    } catch {
      toast.error(t('waitlist.toast.loadError'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  
  
  

  // WAITLIST_OFFER_BLOCKING_MODAL (local notifications; no depende de AuthContext)
  const [notifItems, setNotifItems] = useState([]);

  
  const fetchNotifications = useCallback(async () => {
    if (!api || isClient) return;
    try {
      const res = await api.get('/notifications');
      setNotifItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.warn('fetchNotifications error', e);
    }
  }, [api, isClient]);

  const waitlistOfferNotification = useMemo(() => {
    if (isClient) return null;
    const n = (notifItems || []).find((x) => x?.type === 'WAITLIST_OFFER' && !x?.read);
    if (!n) return null;

    // Si está expirado, no bloqueamos pantalla (backend también expira)
    if (n.expiresAt) {
      const exp = Date.parse(n.expiresAt);
      if (!Number.isNaN(exp) && exp < Date.now()) return null;
    }
    return n;
  }, [notifItems, isClient]);

  const waitlistOfferEntryId = useMemo(() => {
    const n = waitlistOfferNotification;
    if (!n) return null;
    if (n.actionUrl) {
      const m = String(n.actionUrl).match(/\/waitlist\/([^/]+)\//);
      if (m) return m[1];
    }
    return n.entityId || n.entryId || null;
  }, [waitlistOfferNotification]);

  const dismissWaitlistOffer = useCallback(async () => {
    if (!waitlistOfferNotification?.id) return;
    try {
      await api.put(`/notifications/${waitlistOfferNotification.id}/read`);
      await fetchNotifications();
    } catch {
      toast.error(t('waitlist.toast.cannotClose'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, fetchNotifications, waitlistOfferNotification]);

  const confirmWaitlistOffer = useCallback(async () => {
    if (!waitlistOfferEntryId) return;
    try {
      await api.post(`/waitlist/${waitlistOfferEntryId}/force-confirm`);
      if (waitlistOfferNotification?.id) {
        await api.put(`/notifications/${waitlistOfferNotification.id}/read`);
      }
      toast.success(t('waitlist.toast.bookingConfirmed'));
      await fetchNotifications();
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('waitlist.toast.cannotConfirm'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, fetchData, fetchNotifications, waitlistOfferEntryId, waitlistOfferNotification]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

// AUTO: CLIENT select self from /clients
  useEffect(() => {
    if (!isClient) return;
    if (!clients || clients.length === 0) return;
    const self = clients[0];
    const cid = self?.id || self?._id || '';
    if (cid) setCreateClientId(String(cid));
    const nm = self?.fullName || self?.name || user?.fullName || user?.name || '';
    if (nm) setClientQuery(String(nm));
  }, [isClient, clients, user]);
// AUTO: CLIENT self clientId
  useEffect(() => {
    if (!isClient) return;
    const cid = user?.clientId || user?.client?.id || user?.client?._id || '';
    if (cid) setCreateClientId(String(cid));
    const nm = user?.fullName || user?.name || user?.email || '';
    if (nm) setClientQuery(String(nm));
  }, [isClient, user]);

const createEntry = async () => {
    if (!createClientId || !createCalendarId || !createSlot) {
      toast.error(t('waitlist.toast.fillRequired'));
      return;
    }

    try {
      await api.post('/waitlist', {
        clientId: createClientId,
        calendarId: createCalendarId,
        centerId: user?.centerId,
        desiredSlots: [createSlot],
      });

      toast.success(t('waitlist.toast.created'));
      if (!isClient) {
        setCreateClientId('');
        setClientQuery('');
      }
setCreateCalendarId('');
      setCreateDate('');
      setCreateTime('');
      setCreateSlot('');
      fetchData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || t('waitlist.toast.cannotCreate'));
    }
  };

    const clientSuggestions = React.useMemo(() => {
    const q = normalizeForSearch(clientQuery);
    if (!q) return [];
    return (clients || [])
      .filter((c) => normalizeForSearch(getClientLabel(c)).includes(q))
      .slice(0, 8);
  }, [clients, clientQuery, getClientLabel]);

const holdRef = React.useRef(null);

  const stopHold = React.useCallback(() => {
    if (holdRef.current) {
      clearInterval(holdRef.current);
      holdRef.current = null;
    }
  }, []);

  const startHold = React.useCallback((fn) => {
    fn(); // primer paso inmediato
    stopHold();
    holdRef.current = setInterval(fn, 90); // velocidad “rápida”
  }, [stopHold]);

  const getInitials = (name) =>
    (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const statusBadge = (status) => {
    const map = {
      WAITING:   { bg: 'rgba(113,113,122,0.25)', color: '#a1a1aa', label: t('waitlist.status.waiting')   },
      OFFERED:   { bg: 'rgba(234,179,8,0.2)',    color: '#ca8a04', label: t('waitlist.status.offered')   },
      CONFIRMED: { bg: 'rgba(74,222,128,0.2)',   color: '#16a34a', label: t('waitlist.status.confirmed') },
      EXPIRED:   { bg: 'rgba(239,68,68,0.15)',   color: '#dc2626', label: t('waitlist.status.expired')   },
      CANCELLED: { bg: 'rgba(239,68,68,0.15)',   color: '#dc2626', label: t('waitlist.status.cancelled') },
    };
    const s = map[status] || { bg: 'rgba(113,113,122,0.2)', color: '#a1a1aa', label: status };
    return (
      <span style={{
        background: s.bg, color: s.color,
        borderRadius: '9999px', padding: '2px 10px', fontSize: '12px', fontWeight: 500,
      }}>
        {s.label}
      </span>
    );
  };

  const avatarColor = (status) => {
    if (status === 'OFFERED')   return '#92400e';
    if (status === 'CONFIRMED') return '#14532d';
    return '#4c1d95';
  };

  const forceConfirm = async (waitlistId) => {
    try {
      await api.post(`/waitlist/${waitlistId}/force-confirm`);
      toast.success(t('waitlist.toast.forced'));
      fetchData();
    } catch (e) {
      const msg = e?.response?.data?.detail || t('waitlist.toast.cannotForce');
      toast.error(msg);
    }
  };

  const cancelWaitlistEntry = async (waitlistId) => {
    const ok = window.confirm(t('waitlist.cancelConfirm'));
    if (!ok) return;
    try {
      await api.delete(`/waitlist/${waitlistId}`);
      toast.success(t('waitlist.toast.cancelled'));
      fetchData();
    } catch (e) {
      const msg = e?.response?.data?.detail || t('waitlist.toast.cannotCancel');
      toast.error(msg);
    }
  };

    const formatSlotLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);

    const pad = (n) => String(n).padStart(2, '0');
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    const hours = pad(d.getHours());
    const mins = pad(d.getMinutes());
    return `${day}/${month}/${year} ${hours}:${mins}`;
  };

  return (<div className="p-6 h-full overflow-y-auto">
      <h1 className="text-xl mb-4">{t('waitlist.title')}</h1>

      <div className="mb-6 space-y-3">
        <div className="flex flex-col gap-1">
  {isClient ? (
  <Input value={clientQuery || t('clientProfile.access.title')} disabled placeholder={t('waitlist.columns.client')} />
) : (
<div className="relative">
<Input
    disabled={isClient}
    value={clientQuery}
    onChange={(e) => {
      const v = e.target.value;
      setClientQuery(v);
      setCreateClientId('');
      setShowClientSuggestions(true);
      setClientHighlight(-1);

      // si el usuario selecciona exactamente un nombre del datalist, mapeamos a ID
      const match = clients.find((c) => normalizeForSearch(getClientLabel(c)) === normalizeForSearch(v));
      if (match) {
        const id = match.id || match._id;
        if (id) setCreateClientId(String(id));
      }
    }}
    onKeyDown={(e) => {
      if (!showClientSuggestions || clientSuggestions.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setClientHighlight(i => Math.min(i + 1, clientSuggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setClientHighlight(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const idx = clientHighlight >= 0 ? clientHighlight : 0;
        const c = clientSuggestions[idx];
        if (c) {
          const id = c.id || c._id;
          const label = getClientLabel(c);
          setClientQuery(label);
          if (id) setCreateClientId(String(id));
          setShowClientSuggestions(false);
          setClientHighlight(-1);
        }
      } else if (e.key === 'Escape') {
        setShowClientSuggestions(false);
        setClientHighlight(-1);
      }
    }}
    placeholder={t('waitlist.searchClientPlaceholder')}
  />
  {showClientSuggestions && clientSuggestions.length > 0 && (
    <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow">
      {clientSuggestions.map((c, idx) => {
        const id = c.id || c._id;
        const label = getClientLabel(c);
        if (!label) return null;
        return (
          <button
            key={String(id || label)}
            type="button"
            className={`block w-full text-left px-3 py-2 hover:bg-muted ${idx === clientHighlight ? 'bg-muted' : ''}`}
            onClick={() => {
              setClientQuery(label);
              if (id) setCreateClientId(String(id));
              setShowClientSuggestions(false);
              setClientHighlight(-1);
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  )}
</div>
)}
  <div className="text-xs text-zinc-500">
    {createClientId ? t('waitlist.clientSelected') : t('waitlist.searchClient')}
  </div>
</div>
<Select value={createCalendarId} onValueChange={setCreateCalendarId}>
          <SelectTrigger>
            <SelectValue placeholder={t('waitlist.selectService')} />
          </SelectTrigger>
          <SelectContent>
            {calendars.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="date"
          value={createDate}
          onChange={(e) => {
            setCreateDate(e.target.value);
            if (!createTime) setCreateTime('00:00');
          }}
        />

        <div className="flex items-center gap-2 py-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onMouseDown={() => startHold(() => {
              const len = timeOptions.length;
              if (!len) return;
              const idx = Math.max(0, timeOptions.indexOf(createTime || '00:00'));
              const prev = idx <= 0 ? len - 1 : idx - 1;
              setCreateTime(timeOptions[prev]);
            })}
            onMouseUp={stopHold}
            onMouseLeave={stopHold}
            onTouchStart={(e) => {
              e.preventDefault();
              startHold(() => {
                const len = timeOptions.length;
                if (!len) return;
                const idx = Math.max(0, timeOptions.indexOf(createTime || '00:00'));
                const prev = idx <= 0 ? len - 1 : idx - 1;
                setCreateTime(timeOptions[prev]);
              });
            }}
            onTouchEnd={stopHold}
            disabled={!createDate}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="secondary"
            className="min-w-24"
            onClick={() => {
              const len = timeOptions.length;
              if (!len) return;
              const idx = Math.max(0, timeOptions.indexOf(createTime || '00:00'));
              const next = (idx + 1) % len;
              setCreateTime(timeOptions[next]);
            }}
            disabled={!createDate}
          >
            {createTime || '00:00'}
          </Button>

          <Button
            type="button"
            variant="outline"
            onMouseDown={() => startHold(() => {
              const len = timeOptions.length;
              if (!len) return;
              const idx = Math.max(0, timeOptions.indexOf(createTime || '00:00'));
              const next = (idx + 1) % len;
              setCreateTime(timeOptions[next]);
            })}
            onMouseUp={stopHold}
            onMouseLeave={stopHold}
            onTouchStart={(e) => {
              e.preventDefault();
              startHold(() => {
                const len = timeOptions.length;
                if (!len) return;
                const idx = Math.max(0, timeOptions.indexOf(createTime || '00:00'));
                const next = (idx + 1) % len;
                setCreateTime(timeOptions[next]);
              });
            }}
            onTouchEnd={stopHold}
            disabled={!createDate}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          <Input
            type="time"
            step="900"
            value={createTime || '00:00'}
            onChange={(e) => {
              const v = e.target.value;
              // Normaliza a HH:MM
              if (/^\d{2}:\d{2}$/.test(v)) setCreateTime(v);
            }}
            className="w-28"
            disabled={!createDate}
          />

          <div className="text-xs text-zinc-400">
            {!createDate ? t('waitlist.selectDay') : t('waitlist.timeCarousel')}
          </div>
        </div>

        <Button className="w-full md:w-auto" onClick={createEntry}>{t('waitlist.newRequest')}</Button>
      </div>

      {loading ? (
        <Loader2 className="animate-spin" />
      ) : (
        <>
          {/* ── Desktop: tabla ─────────────────────────────────────────── */}
          <div className="hidden md:block max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('waitlist.columns.client')}</TableHead>
                  <TableHead>{t('waitlist.columns.service')}</TableHead>
                  <TableHead>{t('waitlist.columns.slot')}</TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleEntries.map(e => (
                  <TableRow key={e.id}>
                    <TableCell>{e.clientName}</TableCell>
                    <TableCell>{e.calendarName}</TableCell>
                    <TableCell>{formatSlotLocal(e.desiredSlots?.[0])}</TableCell>
                    <TableCell>{statusBadge(e.status)}</TableCell>
                    <TableCell className="text-right">
                      {user?.role !== 'CLIENT' && (e.status === 'WAITING' || e.status === 'OFFERED') && (
                        <Button size="sm" variant="outline" onClick={() => forceConfirm(e.id)}>
                          {t('waitlist.force')}
                        </Button>
                      )}
                      {user?.role !== 'CLIENT' && (
                        <Button size="sm" variant="destructive" className="ml-2" onClick={() => cancelWaitlistEntry(e.id)}>
                          {t('common.cancel')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* ── Móvil: tarjetas ────────────────────────────────────────── */}
          <div className="md:hidden space-y-3">
            {visibleEntries.length === 0 && (
              <p className="text-sm text-zinc-500">{t('waitlist.noEntries')}</p>
            )}

            {visibleEntries.map(e => isClient ? (
              /* Tarjeta CLIENT */
              <div key={e.id} style={{
                background: '#1a1a1a',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: '15px' }}>{e.calendarName}</span>
                  {statusBadge(e.status)}
                </div>

                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)' }}>
                  {formatSlotLocal(e.desiredSlots?.[0])}
                </div>

                {e.status === 'WAITING' && e.position > 0 && (
                  <div style={{
                    fontSize: '13px', fontWeight: 500,
                    color: '#a78bfa',
                    background: 'rgba(124,58,237,0.12)',
                    borderRadius: '6px', padding: '4px 10px', alignSelf: 'flex-start',
                  }}>
                    {t('waitlist.position', { n: e.position })}
                  </div>
                )}

                {(e.status === 'WAITING' || e.status === 'OFFERED') && (
                  <Button size="sm" variant="destructive" className="w-full mt-1" onClick={() => cancelWaitlistEntry(e.id)}>
                    {t('waitlist.cancelRequest')}
                  </Button>
                )}
              </div>
            ) : (
              /* Tarjeta ADMIN */
              <div key={e.id} style={{
                background: '#1a1a1a',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}>
                {/* Cabecera: avatar + nombre + servicio */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '50%',
                    background: avatarColor(e.status),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '14px', color: 'white', flexShrink: 0,
                  }}>
                    {getInitials(e.clientName)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '15px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {e.clientName}
                    </div>
                    <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
                      {e.calendarName}
                    </div>
                  </div>
                  {statusBadge(e.status)}
                </div>

                {/* Slot */}
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)' }}>
                  📅 {formatSlotLocal(e.desiredSlots?.[0])}
                </div>

                {/* Acciones */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  {(e.status === 'WAITING' || e.status === 'OFFERED') && (
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => forceConfirm(e.id)}>
                      {t('waitlist.force')}
                    </Button>
                  )}
                  <Button size="sm" variant="destructive" className="flex-1" onClick={() => cancelWaitlistEntry(e.id)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
